import React, { useState, useEffect } from "react";
import {
  SpeedDial,
  Zoom,
  useScrollTrigger,
  keyframes,
  styled,
  alpha,
} from "@mui/material";
import {
  KeyboardArrowUp as ArrowUpIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Work as WorkIcon,
  ContactMail as ContactIcon,
  Code,
} from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";

// انیمیشن‌های ساده و روان
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(30, 136, 229, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(30, 136, 229, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(30, 136, 229, 0);
  }
`;

// کامپوننت‌های استایل شده
const StyledSpeedDial = styled(SpeedDial)(({ theme, isscrolled }) => ({
  position: "fixed",
  bottom: 32,
  right: 32,
  zIndex: 1000,

  "& .MuiSpeedDial-fab": {
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    color: theme.palette.primary.main,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",

    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      transform: "scale(1.1)",
      animation: `${pulse} 1.5s infinite`,
      boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },

  "&.MuiSpeedDial-open .MuiSpeedDial-fab": {
    transform: "rotate(45deg)",
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
}));

// رنگ‌های مختلف برای هر دکمه
const actionColors = [
  "#2196F3", // آبی - خانه
  "#4CAF50", // سبز - درباره
  "#af4c4cff", // سبز - درباره
  "#FF9800", // نارنجی - پروژه‌ها
  "#9C27B0", // بنفش - تماس
];

const ActionButton = styled("div")(({ theme, index, isopen, color }) => ({
  position: "relative",
  marginBottom: 12,
  display: "flex",
  justifyContent: "flex-end",

  "& .action-fab": {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.background.paper, 0.85),
    color: color,
    border: `1px solid ${alpha(color, 0.3)}`,
    backdropFilter: "blur(15px)",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    opacity: isopen === "true" ? 1 : 0,
    transform: isopen === "true" ? "scale(1)" : "scale(0.5)",
    animation:
      isopen === "true"
        ? `${slideInUp} 0.4s ease-out ${index * 0.1 + 0.2}s both`
        : "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

    "&:hover": {
      backgroundColor: alpha(color, 0.9),
      color: "white",
      transform: "scale(1.15)",
      boxShadow: `0 8px 25px ${alpha(color, 0.3)}`,
      borderColor: color,

      "& svg": {
        transform: "scale(1.2)",
      },
    },
  },

  "& .action-label": {
    position: "absolute",
    right: "calc(100% + 10px)",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    color: theme.palette.text.primary,
    padding: "8px 16px",
    borderRadius: 20,
    fontSize: "0.875rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    backdropFilter: "blur(10px)",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    opacity: 0,
    visibility: "hidden",
    transition: "all 0.3s ease",
    zIndex: 1001,
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
  },

  "&:hover .action-label": {
    opacity: 1,
    visibility: "visible",
    transform: "translateY(-50%) translateX(-5px)",
  },
}));

const ScrollNavigationMinimal = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const trigger = useScrollTrigger({
    threshold: 300,
    disableHysteresis: true,
  });

  useEffect(() => {
    setIsScrolled(trigger);
  }, [trigger]);

  const actions = [
    {
      icon: <HomeIcon />,
      name: "Hero",
      targetId: "home-section",
    },
    {
      icon: <InfoIcon />,
      name: "Who Am I",
      targetId: "about-section",
    },
    {
      icon: <Code />,
      name: "Skills",
      targetId: "skills-section",
    },
    {
      icon: <WorkIcon />,
      name: "Live Projects",
      targetId: "projects-section",
    },
    {
      icon: <ContactIcon />,
      name: "Contact Me",
      targetId: "contact-section",
    },
  ];

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleActionClick = (targetId) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    handleClose();
  };

  return (
    <Zoom
      in={isScrolled}
      style={{
        transitionDelay: isScrolled ? "100ms" : "0ms",
        transformOrigin: "bottom right",
      }}
    >
      <StyledSpeedDial
        ariaLabel="پیمایش صفحه"
        icon={
          <ArrowUpIcon
            sx={{
              transition: "transform 0.3s ease",
              transform: open ? "rotate(180deg)" : "rotate(0)",
            }}
          />
        }
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
        direction="up"
        isscrolled={isScrolled.toString()}
        sx={{
          "& .MuiSpeedDial-actions": {
            paddingBottom: 4,
            flexDirection: "column-reverse",
            gap: 1,
          },
        }}
      >
        {actions.map((action, index) => (
          <ActionButton
            key={action.name}
            sx={{ mb: "1rem" }}
            index={index}
            isopen={open.toString()}
            color={actionColors[index]}
            onClick={() => handleActionClick(action.targetId)}
          >
            <div className="action-fab">
              {React.cloneElement(action.icon, {
                sx: {
                  fontSize: "1.5rem",
                  transition: "transform 0.2s ease",
                },
              })}
            </div>
            <div className="action-label">{action.name}</div>
          </ActionButton>
        ))}
      </StyledSpeedDial>
    </Zoom>
  );
};

export default ScrollNavigationMinimal;
